import React from 'react';
import { X, ShieldCheck, ScrollText, CheckCircle2 } from 'lucide-react';

interface PrivacyPolicyModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-2xl rounded-[2.5rem] shadow-2xl relative flex flex-col max-h-[90vh] overflow-hidden border border-surface-100">
                {/* Header */}
                <div className="p-8 border-b border-surface-50 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-brand-50 rounded-2xl flex items-center justify-center text-brand-600 shadow-inner border border-brand-100/50">
                            <ShieldCheck size={26} />
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-surface-900 tracking-tight">Privacidade & LGPD</h3>
                            <p className="text-xs text-surface-400 font-bold uppercase tracking-widest">ZapShop • Sua Segurança Primeiro</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-surface-100 text-surface-400 transition-colors"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-8 overflow-y-auto font-sans leading-relaxed text-surface-600">
                    <div className="space-y-8">
                        <section>
                            <div className="flex items-center gap-2 mb-3">
                                <ScrollText size={18} className="text-brand-600" />
                                <h4 className="font-bold text-surface-900 uppercase text-sm tracking-widest">Sobre seus Dados</h4>
                            </div>
                            <p className="text-sm">
                                Na ZapShop, levamos sua privacidade a sério. Em conformidade com a <strong>Lei Geral de Proteção de Dados (LGPD)</strong> do Brasil, informamos que coletamos dados de navegação e cookies apenas para garantir o funcionamento técnico da plataforma e melhorar sua experiência de uso.
                            </p>
                        </section>

                        <section className="bg-surface-50 p-6 rounded-3xl space-y-4">
                            <h4 className="font-bold text-surface-900 text-sm">O que coletamos?</h4>
                            <ul className="space-y-3">
                                {[
                                    'Dados técnicos como endereço IP e tipo de navegador para segurança.',
                                    'Cookies essenciais para manter sua sessão ativa e preferências de idioma.',
                                    'Dados de navegação anônimos para análise de desempenho e melhorias.',
                                    'Informações de contato (WhatsApp/Email) fornecidas voluntariamente para criação de conta.'
                                ].map((item, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm">
                                        <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>

                        <section>
                            <h4 className="font-bold text-surface-900 text-sm mb-3 uppercase tracking-widest">Seus Direitos</h4>
                            <p className="text-sm italic">
                                Você tem o direito de acessar, corrigir ou excluir seus dados a qualquer momento. Suas informações nunca são vendidas a terceiros e são armazenadas em servidores seguros com criptografia de ponta-a-ponta.
                            </p>
                        </section>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-8 bg-surface-50/50 border-t border-surface-50 flex justify-end shrink-0">
                    <button
                        onClick={onClose}
                        className="bg-brand-600 hover:bg-brand-700 text-white px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-brand-600/20 active:scale-95"
                    >
                        Entendido
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicyModal;
